import { useState, useCallback, useRef, type FormEvent } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useApiQuery } from '@/hooks/useApiQuery';

interface Company { id: number; name: string; }

interface AddCustomerModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const INDIVIDUAL_DOC_TYPES = [
  'Aadhaar',
  'PAN',
  'Voter ID',
  'Passport',
  'Birth Certificate',
  '10th Marks Card',
  'Driving License',
  'Bank Passbook',
];

const ENTITY_DOC_TYPES = [
  'PAN',
  'GST Registration',
  'Certificate of Incorporation',
  'MOA & AOA',
  'Address Proof',
  'Bank Statement',
  'Partnership Deed',
  'Trust Deed',
];

const API_BASE = import.meta.env.VITE_API_URL || 'https://ekyc.tumkurconnect.com/api/v1';

function calculateAge(dob: string): number | null {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

export default function AddCustomerModal({ open, onClose, onSuccess }: AddCustomerModalProps) {
  const { accessToken, user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';

  const { data: companiesData } = useApiQuery<Company[]>({
    endpoint: '/companies',
    enabled: isSuperAdmin && open,
  });

  const companies = companiesData || [];

  const { data: customerTypesData } = useApiQuery<{ id: number; name: string }[]>({
    endpoint: '/masters/customer-types',
    enabled: open,
  });
  const customerTypes = customerTypesData || [];

  const { data: idTypesData } = useApiQuery<{ id: number; name: string; format_hint?: string }[]>({
    endpoint: '/masters/id-types',
    enabled: open,
  });
  const idTypes = idTypesData || [];

  // Fetch users for managed_by dropdown
  const { data: usersData } = useApiQuery<{ data?: { id: number; full_name: string }[] } | { id: number; full_name: string }[]>({
    endpoint: '/users',
    enabled: open,
  });
  const usersList = Array.isArray(usersData)
    ? usersData
    : (usersData as { data?: { id: number; full_name: string }[] } | null)?.data || [];

  const [step, setStep] = useState(1);

  // Basic info
  const [customerTypeId, setCustomerTypeId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [managedBy, setManagedBy] = useState('');

  // Identity
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [idTypeId, setIdTypeId] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [nationality, setNationality] = useState('');
  const [occupation, setOccupation] = useState('');
  const [notes, setNotes] = useState('');

  // Documents for individuals
  const [individualDocs, setIndividualDocs] = useState<Record<string, File | null>>({
    Aadhaar: null,
    PAN: null,
    'Voter ID': null,
    Passport: null,
    'Birth Certificate': null,
    '10th Marks Card': null,
    'Driving License': null,
    'Bank Passbook': null,
  });

  // Linked companies for non-individuals
  const [companyCount, setCompanyCount] = useState(1);
  const [companyNames, setCompanyNames] = useState<string[]>(['']);
  const [companyDocs, setCompanyDocs] = useState<Record<number, Record<string, File | null>>>({
    0: {},
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>();

  const selectedCustomerType = customerTypes.find((t) => String(t.id) === customerTypeId);
  const isIndividual = selectedCustomerType?.name?.toLowerCase() === 'individual';

  const resetForm = useCallback(() => {
    setStep(1);
    setCustomerTypeId('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setCompanyId('');
    setManagedBy('');
    setDateOfBirth('');
    setGender('');
    setIdTypeId('');
    setIdNumber('');
    setAddress('');
    setCity('');
    setState('');
    setCountry('');
    setPostalCode('');
    setNationality('');
    setOccupation('');
    setNotes('');
    setIndividualDocs({
      Aadhaar: null, PAN: null, 'Voter ID': null, Passport: null,
      'Birth Certificate': null, '10th Marks Card': null, 'Driving License': null, 'Bank Passbook': null,
    });
    setCompanyCount(1);
    setCompanyNames(['']);
    setCompanyDocs({ 0: {} });
    setError(null);
    setLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleCompanyCountChange = (count: number) => {
    setCompanyCount(count);
    const newNames = Array.from({ length: count }, (_, i) => companyNames[i] || '');
    setCompanyNames(newNames);
    const newDocs: Record<number, Record<string, File | null>> = {};
    for (let i = 0; i < count; i++) {
      newDocs[i] = companyDocs[i] || {};
    }
    setCompanyDocs(newDocs);
  };

  const updateCompanyName = (index: number, value: string) => {
    setCompanyNames((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const updateCompanyDoc = (companyIndex: number, docType: string, file: File | null) => {
    setCompanyDocs((prev) => ({
      ...prev,
      [companyIndex]: { ...prev[companyIndex], [docType]: file },
    }));
  };

  const uploadFile = async (file: File, customerId: number, docType: string): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('docType', docType);
    formData.append('customerId', String(customerId));
    formData.append('docCategory', 'identity');

    const res = await fetch(`${API_BASE}/documents`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: formData,
    });
    const json = await res.json();
    if (!res.ok) {
      throw new Error(json.message || `Failed to upload ${docType}`);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate step 1
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError('First name, last name, and email are required');
      setStep(1);
      return;
    }

    if (isSuperAdmin && !companyId && !isIndividual) {
      setError('Please select a company for this non-individual customer');
      setStep(1);
      return;
    }

    // Validate individual
    if (isIndividual) {
      if (!dateOfBirth) {
        setError('Date of birth is required for individual customers');
        setStep(2);
        return;
      }
      const age = calculateAge(dateOfBirth);
      if (age === null || age < 18) {
        setError(`Customer must be 18 years or older. Current age: ${age ?? 'unknown'}`);
        setStep(2);
        return;
      }
      const uploadedCount = Object.values(individualDocs).filter(Boolean).length;
      if (uploadedCount < 4) {
        setError(`At least 4 eKYC documents are required. You have uploaded ${uploadedCount}.`);
        setStep(3);
        return;
      }
    }

    // Validate non-individual
    if (!isIndividual) {
      const validNames = companyNames.filter((n) => n.trim()).length;
      if (validNames < companyCount) {
        setError(`Please enter names for all ${companyCount} linked companies.`);
        setStep(3);
        return;
      }
    }

    setLoading(true);

    try {
      // 1. Create customer
      const customerBody: Record<string, unknown> = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        customerTypeId: customerTypeId ? Number(customerTypeId) : null,
        phone: phone.trim() || null,
        dateOfBirth: dateOfBirth || null,
        gender: gender || null,
        address: address.trim() || null,
        city: city.trim() || null,
        state: state.trim() || null,
        country: country.trim() || null,
        postalCode: postalCode.trim() || null,
        idTypeId: idTypeId ? Number(idTypeId) : null,
        idNumber: idNumber.trim() || null,
        nationality: nationality.trim() || null,
        occupation: occupation.trim() || null,
        notes: notes.trim() || null,
        managedBy: managedBy ? Number(managedBy) : null,
      };

      if (isSuperAdmin && companyId) {
        customerBody.companyId = Number(companyId);
      }

      if (!isIndividual) {
        customerBody.linkedCompanies = companyNames.filter((n) => n.trim());
      }

      const customerRes = await fetch(`${API_BASE}/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(customerBody),
      });
      const customerJson = await customerRes.json();
      if (!customerRes.ok) {
        throw new Error(customerJson.message || 'Failed to create customer');
      }

      const customerId = customerJson.data?.id;
      if (!customerId) {
        throw new Error('Customer created but no ID returned');
      }

      // 2. Upload individual documents
      if (isIndividual) {
        for (const [docType, file] of Object.entries(individualDocs)) {
          if (file) {
            await uploadFile(file, customerId, docType);
          }
        }
      }

      // 3. Upload company documents for non-individual
      if (!isIndividual) {
        for (let i = 0; i < companyCount; i++) {
          const docs = companyDocs[i] || {};
          for (const [docType, file] of Object.entries(docs)) {
            if (file) {
              await uploadFile(file, customerId, `${companyNames[i]} - ${docType}`);
            }
          }
        }
      }

      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create customer');
      setLoading(false);
    }
  };

  if (!open) return null;

  const renderStepIndicator = () => (
    <div className="flex items-center gap-2 mb-6">
      {[1, 2, 3].map((s) => (
        <div key={s} className="flex items-center gap-2">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
              s === step
                ? 'bg-brand-600 text-white'
                : s < step
                  ? 'bg-brand-100 text-brand-700'
                  : 'bg-stone-100 text-stone-400'
            }`}
          >
            {s < step ? <i className="ri-check-line" /> : s}
          </div>
          <span className={`text-xs font-medium ${s === step ? 'text-brand-700' : 'text-stone-400'}`}>
            {s === 1 ? 'Basic Info' : s === 2 ? 'Identity' : 'Documents'}
          </span>
          {s < 3 && <div className={`w-8 h-px ${s < step ? 'bg-brand-300' : 'bg-stone-200'}`} />}
        </div>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
          <div>
            <h2 className="text-lg font-bold text-stone-900">Add Customer</h2>
            <p className="text-stone-400 text-xs mt-0.5">
              Step {step} of 3 &mdash; {step === 1 ? 'Basic Information' : step === 2 ? 'Identity Details' : 'eKYC Documents'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {renderStepIndicator()}

          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2 mb-4">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}

          {/* STEP 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-4">
              {isSuperAdmin && (
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Company</label>
                  <select
                    value={companyId}
                    onChange={(e) => setCompanyId(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                  >
                    <option value="">Select a company (optional)</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <p className="text-stone-400 text-xs mt-1">Leave blank to use the default company. Required for non-individual customers.</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Managed By</label>
                <select
                  value={managedBy}
                  onChange={(e) => setManagedBy(e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                >
                  <option value="">Unassigned</option>
                  {usersList.map((u) => (
                    <option key={u.id} value={u.id}>{u.full_name || `User #${u.id}`}</option>
                  ))}
                </select>
                <p className="text-stone-400 text-xs mt-1">Assign a staff member to manage this customer profile.</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Customer Type *</label>
                <select
                  value={customerTypeId}
                  onChange={(e) => {
                    setCustomerTypeId(e.target.value);
                    setStep(1);
                  }}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                >
                  <option value="">Select customer type</option>
                  {customerTypes.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">First Name *</label>
                  <input
                    required
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Last Name *</label>
                  <input
                    required
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Email *</label>
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={500}
                  rows={3}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 resize-none"
                />
                <p className="text-stone-400 text-[10px] text-right mt-0.5">{notes.length}/500</p>
              </div>
            </div>
          )}

          {/* STEP 2: Identity */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">
                    Date of Birth {isIndividual && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    required={isIndividual}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                  {isIndividual && dateOfBirth && (
                    <p className={`text-xs mt-1 ${calculateAge(dateOfBirth)! >= 18 ? 'text-emerald-600' : 'text-red-500'}`}>
                      Age: {calculateAge(dateOfBirth) ?? 'unknown'} {calculateAge(dateOfBirth)! >= 18 ? '(Eligible)' : '(Must be 18+)'}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Gender</label>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                    <option value="prefer_not_to_say">Prefer not to say</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">ID Type</label>
                  <select
                    value={idTypeId}
                    onChange={(e) => setIdTypeId(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                  >
                    <option value="">Select ID type</option>
                    {idTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">ID Number</label>
                  <input
                    type="text"
                    value={idNumber}
                    onChange={(e) => setIdNumber(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Address</label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">City</label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">State</label>
                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Country</label>
                  <input
                    type="text"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Postal Code</label>
                  <input
                    type="text"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Nationality</label>
                  <input
                    type="text"
                    value={nationality}
                    onChange={(e) => setNationality(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Occupation</label>
                  <input
                    type="text"
                    value={occupation}
                    onChange={(e) => setOccupation(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Documents */}
          {step === 3 && (
            <div className="space-y-5">
              {/* Individual Documents */}
              {isIndividual && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-stone-900">eKYC Documents</h3>
                    <span className="text-xs text-stone-500">
                      {Object.values(individualDocs).filter(Boolean).length} of 8 uploaded
                      <span className="text-red-500 ml-1">(min 4 required)</span>
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {INDIVIDUAL_DOC_TYPES.map((docType) => {
                      const file = individualDocs[docType];
                      return (
                        <div
                          key={docType}
                          className={`border rounded-xl p-3 transition-colors ${file ? 'border-brand-300 bg-brand-50/30' : 'border-stone-200'}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-stone-700">{docType}</span>
                            {file && <i className="ri-check-line text-brand-600 text-sm" />}
                          </div>
                          <div className="border-2 border-dashed border-stone-200 rounded-lg p-3 text-center hover:border-brand-400 transition-colors">
                            <input
                              type="file"
                              ref={(el) => { if (el) fileInputRefs.current[`ind_${docType}`] = el; }}
                              onChange={(e) => {
                                setIndividualDocs((prev) => ({ ...prev, [docType]: e.target.files?.[0] || null }));
                              }}
                              accept="image/jpeg,image/png,image/jpg,application/pdf"
                              className="hidden"
                              id={`ind-file-${docType}`}
                            />
                            <label htmlFor={`ind-file-${docType}`} className="cursor-pointer flex flex-col items-center gap-1">
                              <i className="ri-upload-cloud-line text-stone-300 text-xl" />
                              <span className="text-stone-500 text-xs">
                                {file ? file.name : 'Click to upload'}
                              </span>
                              <span className="text-stone-400 text-[10px]">JPG, PNG, PDF</span>
                            </label>
                          </div>
                          {file && (
                            <button
                              type="button"
                              onClick={() => {
                                setIndividualDocs((prev) => ({ ...prev, [docType]: null }));
                                const input = fileInputRefs.current[`ind_${docType}`];
                                if (input) {
                                  input.value = '';
                                }
                              }}
                              className="mt-1.5 text-red-500 text-xs hover:underline"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Non-Individual: Linked Companies */}
              {!isIndividual && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-xs font-medium text-stone-500 mb-1">Number of Linked Companies</label>
                    <select
                      value={companyCount}
                      onChange={(e) => handleCompanyCountChange(Number(e.target.value))}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                    >
                      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>

                  {Array.from({ length: companyCount }).map((_, idx) => (
                    <div key={idx} className="border border-stone-200 rounded-xl p-4 space-y-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-brand-100 text-brand-700 text-xs font-bold flex items-center justify-center">
                          {idx + 1}
                        </span>
                        <h4 className="text-sm font-bold text-stone-900">Linked Company {idx + 1}</h4>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-stone-500 mb-1">Company Name *</label>
                        <input
                          type="text"
                          value={companyNames[idx] || ''}
                          onChange={(e) => updateCompanyName(idx, e.target.value)}
                          required
                          className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                        />
                      </div>

                      <div>
                        <p className="text-xs font-medium text-stone-500 mb-2">Upload eKYC Documents</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {ENTITY_DOC_TYPES.map((docType) => {
                            const file = companyDocs[idx]?.[docType];
                            return (
                              <div
                                key={docType}
                                className={`border rounded-lg p-2.5 transition-colors ${file ? 'border-brand-300 bg-brand-50/30' : 'border-stone-200'}`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-medium text-stone-700">{docType}</span>
                                  {file && <i className="ri-check-line text-brand-600 text-xs" />}
                                </div>
                                <div className="border border-dashed border-stone-200 rounded-md p-2 text-center hover:border-brand-400 transition-colors">
                                  <input
                                    type="file"
                                    onChange={(e) => updateCompanyDoc(idx, docType, e.target.files?.[0] || null)}
                                    accept="image/jpeg,image/png,image/jpg,application/pdf"
                                    className="hidden"
                                    id={`comp-${idx}-file-${docType}`}
                                  />
                                  <label htmlFor={`comp-${idx}-file-${docType}`} className="cursor-pointer flex flex-col items-center gap-0.5">
                                    <i className="ri-upload-cloud-line text-stone-300 text-sm" />
                                    <span className="text-stone-500 text-[10px]">
                                      {file ? file.name : 'Upload'}
                                    </span>
                                  </label>
                                </div>
                                {file && (
                                  <button
                                    type="button"
                                    onClick={() => updateCompanyDoc(idx, docType, null)}
                                    className="mt-1 text-red-500 text-[10px] hover:underline"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="pt-4 mt-4 border-t border-stone-100 flex items-center justify-between">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2.5 rounded-full text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors whitespace-nowrap"
            >
              Cancel
            </button>
            <div className="flex items-center gap-3">
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className="px-4 py-2.5 rounded-full text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors whitespace-nowrap"
                >
                  Back
                </button>
              )}
              {step < 3 ? (
                <button
                  type="button"
                  onClick={() => {
                    if (step === 1 && (!firstName.trim() || !lastName.trim() || !email.trim())) {
                      setError('First name, last name, and email are required');
                      return;
                    }
                    if (step === 1 && isSuperAdmin && !companyId && !isIndividual) {
                      setError('Please select a company for this non-individual customer');
                      return;
                    }
                    setError(null);
                    setStep(step + 1);
                  }}
                  className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors whitespace-nowrap"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-brand-600 hover:bg-brand-700 disabled:bg-stone-300 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Create Customer'
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
